import {
  Badge,
  Box,
  Button,
  Card,
  Grid,
  Group,
  Menu,
  Paper,
  ScrollArea,
  SegmentedControl,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconBookmark,
  IconChevronDown,
  IconDownload,
  IconLayersIntersect,
  IconShape,
  IconSparkles,
  IconTypography,
} from '@tabler/icons-react';
import React, { useState } from 'react';

import { PRESET_TEMPLATES } from '../presets';
import { useVectorCadStore } from '../stores/vector-cad-store';
import { BackerPanel } from './BackerPanel';
import { ExportModal } from './ExportModal';
import { TypographyPanel } from './TypographyPanel';
import { ViewportCanvas } from './ViewportCanvas';
import { WeldScorePanel } from './WeldScorePanel';

export function VectorCadStudio() {
  const [activeTab, setActiveTab] = useState<string | null>('typography');
  const [exportOpened, { open: openExport, close: closeExport }] = useDisclosure(false);

  const { unit, setUnit, activePresetId, applyPreset } = useVectorCadStore();

  const currentPreset =
    PRESET_TEMPLATES.find((p) => p.id === activePresetId) || PRESET_TEMPLATES[0];

  return (
    <Box style={{ height: 'calc(100vh - 110px)', display: 'flex', flexDirection: 'column' }}>
      {/* Studio Header Toolbar */}
      <Paper withBorder p='xs' px='md' radius='md' mb='md'>
        <Group justify='space-between'>
          {/* Title & Preset Picker */}
          <Group gap='md'>
            <Group gap='xs'>
              <Box className='brand-mark' style={{ width: 28, height: 28, fontSize: 14 }}>
                C
              </Box>
              <div>
                <Group gap={6}>
                  <Title order={4}>Laser CAD Studio</Title>
                  <Badge size='xs' variant='light' color='blue'>
                    Cuttle-Engine
                  </Badge>
                </Group>
              </div>
            </Group>

            {/* Presets Dropdown */}
            <Menu shadow='md' width={260}>
              <Menu.Target>
                <Button
                  size='xs'
                  variant='light'
                  color='gray'
                  leftSection={<IconBookmark size={14} />}
                  rightSection={<IconChevronDown size={14} />}
                >
                  {currentPreset.name}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Design Templates</Menu.Label>
                {PRESET_TEMPLATES.map((preset) => (
                  <Menu.Item
                    key={preset.id}
                    onClick={() => applyPreset(preset.id)}
                    leftSection={<IconSparkles size={14} />}
                  >
                    <div>
                      <Text size='xs' fw={600}>
                        {preset.name}
                      </Text>
                      <Text size='10px' c='dimmed'>
                        {preset.description}
                      </Text>
                    </div>
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          </Group>

          {/* Unit Toggle & Export Actions */}
          <Group gap='sm'>
            <SegmentedControl
              size='xs'
              value={unit}
              onChange={(val) => setUnit(val as 'mm' | 'inch')}
              data={[
                { label: 'mm', value: 'mm' },
                { label: 'inch', value: 'inch' },
              ]}
            />

            <Button
              size='xs'
              variant='filled'
              color='blue'
              leftSection={<IconDownload size={14} />}
              onClick={openExport}
            >
              Export Laser CAD
            </Button>
          </Group>
        </Group>
      </Paper>

      {/* Main Studio Body: Controls + Viewport */}
      <Grid style={{ flex: 1, minHeight: 0 }} gap='md'>
        {/* Left Control Sidebar */}
        <Grid.Col
          span={{ base: 12, md: 4, lg: 3.8 }}
          style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <Card
            withBorder
            p='xs'
            radius='md'
            style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
          >
            <Tabs
              value={activeTab}
              onChange={setActiveTab}
              variant='pills'
              style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
            >
              <Tabs.List grow mb='xs'>
                <Tabs.Tab value='typography' leftSection={<IconTypography size={14} />}>
                  Quotes
                </Tabs.Tab>
                <Tabs.Tab value='weld-score' leftSection={<IconLayersIntersect size={14} />}>
                  Weld & Score
                </Tabs.Tab>
                <Tabs.Tab value='backer' leftSection={<IconShape size={14} />}>
                  Backer & Base
                </Tabs.Tab>
              </Tabs.List>

              <Box style={{ flex: 1, overflow: 'hidden' }}>
                <ScrollArea h='100%' type='hover' offsetScrollbars>
                  <Box p={4}>
                    <Tabs.Panel value='typography'>
                      <TypographyPanel />
                    </Tabs.Panel>
                    <Tabs.Panel value='weld-score'>
                      <WeldScorePanel />
                    </Tabs.Panel>
                    <Tabs.Panel value='backer'>
                      <BackerPanel />
                    </Tabs.Panel>
                  </Box>
                </ScrollArea>
              </Box>
            </Tabs>
          </Card>
        </Grid.Col>

        {/* Right Canvas Viewport */}
        <Grid.Col span={{ base: 12, md: 8, lg: 8.2 }} style={{ height: '100%' }}>
          <ViewportCanvas />
        </Grid.Col>
      </Grid>

      {/* Export Modal */}
      <ExportModal opened={exportOpened} onClose={closeExport} />
    </Box>
  );
}
